import React, { useState, useEffect, useContext } from 'react';
import gameService from '../../../services/gameService';
import { AuthContext } from '../../../context/AuthContext';
import { Button, Spinner, Card, InputGroup, FormControl, ProgressBar } from 'react-bootstrap';
import {
  FaDice,
  FaCoins,
  FaCheckCircle,
  FaTimesCircle,
  FaDiceOne,
  FaDiceTwo,
  FaDiceThree,
  FaDiceFour,
  FaDiceFive,
  FaDiceSix,
  FaFistRaised,
  FaPercent,
  FaInfoCircle,
  FaHistory,
  FaArrowLeft,
  FaTimes,
  FaCrown,
} from 'react-icons/fa';
import './MenelTwoDice.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const MenelTwoDice = () => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const userId = auth.user?.id;
  const gameId = 14;

  const [betAmount, setBetAmount] = useState(1);
  const [chosenNumber, setChosenNumber] = useState(2);
  const [result, setResult] = useState(null);
  const [dice, setDice] = useState([]);
  const [sum, setSum] = useState(null);
  const [newBalance, setNewBalance] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isRolling, setIsRolling] = useState(false);

  const navigate = useNavigate();

  const getDiceIcon = (number, size = '2em') => {
    const icons = [
      <FaDiceOne size={size} />, <FaDiceTwo size={size} />, <FaDiceThree size={size} />,
      <FaDiceFour size={size} />, <FaDiceFive size={size} />, <FaDiceSix size={size} />
    ];
    return icons[number - 1];
  };

  const fetchHistory = async () => {
    if (userId) {
      try {
        const data = await gameService.getPlayerHistory(userId, gameId);
        setHistory(data);
      } catch {
        setError(t('meneldie.errorHistory'));
      }
    }
  };

  useEffect(() => {
    fetchHistory();
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, [userId]);

  const handlePlay = async () => {
    if (betAmount < 1) {
      setError(t('meneldie.minBet'));
      return;
    }
    setLoading(true);
    setResult(null);
    setError(null);
    setIsRolling(true);

    try {
      const response = await gameService.playMenelTwoDice(betAmount, chosenNumber);
      setTimeout(() => {
        setResult(response.result);
        setDice(response.dice);
        setSum(response.sum);
        setNewBalance(response.newBalance);
        setLoading(false);
        setIsRolling(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setLoading(false);
      setIsRolling(false);
    }
  };

  return (
    <div className="meneldie-container d-flex justify-content-center align-items-center">
      {isLoading ? (
        <div className="meneldie-loading-container">
          <h2 className="meneldie-loading-text">{t('meneldie.loading')}</h2>
          <ProgressBar animated now={100} className="meneldie-loading-bar" />
        </div>
      ) : (
        <Card className="text-center meneldie-card shadow-lg">
          <Card.Body>
            <div className="position-relative mb-4">
              <Button variant="secondary" onClick={() => navigate('/sessions')} className="meneldie-back-button">
                <FaArrowLeft className="me-2" />
              </Button>
              <h1 className="meneldie-casino-title text-center">
                <FaCrown className="text-warning me-2" /> MENEL Two Dice <FaCrown className="text-warning ms-2" />
              </h1>
            </div>

            <div className="d-flex justify-content-around mb-3">
              <Button className="meneldie-button" onClick={() => setShowRulesModal(true)}>
                <FaInfoCircle className="me-2" /> {t('meneldie.rules')}
              </Button>
              <Button className="meneldie-button" onClick={() => setShowHistoryModal(true)}>
                <FaHistory className="me-2" /> {t('meneldie.history')}
              </Button>
            </div>

            <InputGroup className="meneldie-bet-input-group mb-3">
              <InputGroup.Text><FaCoins /></InputGroup.Text>
              <FormControl
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                min="1"
              />
              <InputGroup.Text>MENEL</InputGroup.Text>
            </InputGroup>

            <div className="mb-3">
              <label>{t('meneldie.chooseNumber')}</label>
              <div className="d-flex flex-wrap justify-content-around">
                {Array.from({ length: 11 }, (_, i) => i + 2).map((num) => (
                  <div
                    key={num}
                    className={`meneldie-dice-option ${chosenNumber === num ? 'selected' : ''}`}
                    onClick={() => setChosenNumber(num)}
                  >
                    {num}
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-center mb-3">
              <Button onClick={handlePlay} disabled={loading || isRolling} className="me-2">
                {loading || isRolling ? <Spinner animation="border" size="sm" /> : <><FaFistRaised className="me-2" /> {t('meneldie.roll')}</>}
              </Button>
              <Button onClick={() => { setResult(null); setDice([]); setSum(null); setNewBalance(null); }} disabled={!result}>
                <FaTimes className="me-2" /> {t('meneldie.reset')}
              </Button>
            </div>

            {isRolling && (
              <div className="meneldie-dice-animation mt-4">
                <div className="meneldie-dice animate__animated animate__rubberBand">
                  {getDiceIcon(1)}
                </div>
                <div className="meneldie-dice animate__animated animate__rubberBand">
                  {getDiceIcon(1)}
                </div>
                <p>{t('meneldie.rolling')}</p>
              </div>
            )}

            {result && (
              <div className="meneldie-result mt-4">
                {result === 'win' ? (
                  <FaCheckCircle className="meneldie-result-icon meneldie-win-icon animate__animated animate__bounceIn" />
                ) : (
                  <FaTimesCircle className="meneldie-result-icon meneldie-lose-icon animate__animated animate__shakeX" />
                )}
                <h3 className="animate__animated animate__fadeIn">{t(`meneldie.${result}`)}</h3>
                <div className="meneldie-dice-result d-flex justify-content-center gap-3">
                  {dice.map((d, i) => (
                    <span key={i}>{getDiceIcon(d, '4em')}</span>
                  ))}
                </div>
                <p className="animate__animated animate__fadeIn">
                  {t('meneldie.newBalance')}: {newBalance} MENEL
                </p>
              </div>
            )}

            {error && <p className="text-danger mt-3">{error}</p>}
          </Card.Body>
        </Card>
      )}

      {/* Modal de Reglas */}
      {showRulesModal && (
        <div className="meneldie-custom-modal">
          <div className="meneldie-modal-content">
            <div className="meneldie-modal-header">
              <h2><FaInfoCircle className="me-2" /> {t('meneldie.rules')}</h2>
              <button onClick={() => setShowRulesModal(false)}><FaTimes /></button>
            </div>
            <div className="meneldie-modal-body">
              <p>{t('meneldie.rulesText')}</p>
              <ul>
                <li><FaDice /> {t('meneldie.chooseNumber')} 2-12</li>
                <li><FaCoins /> {t('meneldie.minBet')}</li>
                <li><FaPercent /> {t('meneldie.winChance')}</li>
                <li><FaFistRaised /> {t('meneldie.reward')}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Historial */}
      {showHistoryModal && (
        <div className="meneldie-custom-modal">
          <div className="meneldie-modal-content">
            <div className="meneldie-modal-header">
              <h2><FaHistory className="me-2" /> {t('meneldie.history')}</h2>
              <button onClick={() => setShowHistoryModal(false)}><FaTimes /></button>
            </div>
            <div className="meneldie-modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {history.length > 0 ? (
                <ul className="list-group">
                  {history.map((entry, index) => (
                    <li key={index} className="list-group-item">
                      <strong>{t(`meneldie.${entry.result}`)}:</strong> {entry.score} MENEL<br />
                      <strong>{t('meneldie.details')}:</strong> {entry.details}<br />
                      <strong>{t('meneldie.date')}:</strong> {new Date(entry.created_at).toLocaleString()}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('meneldie.noPlays')}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenelTwoDice;
